import React from 'react'
import { Card, CardHeader, CardContent, Label, Button, Select, Switch } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { useUserDateFormatter } from '@/modules/auth/hooks/useUserTimezone'
import { usePushPreferences, type PushDayKey, type PushScheduleMode } from '../hooks/usePushPreferences'

const DAY_LABELS: Record<PushDayKey, string> = {
  mon: 'Segunda\u2011feira',
  tue: 'Terça\u2011feira',
  wed: 'Quarta\u2011feira',
  thu: 'Quinta\u2011feira',
  fri: 'Sexta\u2011feira',
  sat: 'Sábado',
  sun: 'Domingo',
}

type SnoozePreset = {
  label: string
  ms: number
}

const SNOOZE_PRESETS: SnoozePreset[] = [
  { label: 'Por 30 minutos', ms: 30 * 60 * 1000 },
  { label: 'Por 1 hora', ms: 60 * 60 * 1000 },
  { label: 'Por 2 horas', ms: 2 * 60 * 60 * 1000 },
  { label: 'Até amanhã', ms: 24 * 60 * 60 * 1000 },
  { label: 'Até a próxima semana', ms: 7 * 24 * 60 * 60 * 1000 },
]

const TIME_OPTIONS: { value: number; label: string }[] = (() => {
  const result: { value: number; label: string }[] = []
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const value = h * 60 + m
      const label = `${String(h).padStart(2, '0')}:${m === 0 ? '00' : '30'}`
      result.push({ value, label })
    }
  }
  return result
})()

function minuteToOptionValue(min: number): string {
  const safe = Math.max(0, Math.min(1439, min))
  return String(safe - (safe % 30))
}

function optionValueToMinute(value: string): number {
  const n = Number(value)
  if (!Number.isFinite(n)) return 0
  return Math.max(0, Math.min(1439, n))
}

function formatSnoozeDescription(iso: string | null | undefined, formatDate: (iso?: string | null) => string): string | null {
  if (!iso) return null
  try {
    return formatDate(iso)
  } catch {
    return null
  }
}

function PushScheduleSettingsCard() {
  const { prefs, loading, saving, update, save } = usePushPreferences()
  const { add: toast } = useToast()
  const { formatUserDateTime } = useUserDateFormatter()

  const busy = loading || saving || !prefs

  async function handleSave() {
    if (!prefs) return
    const ok = await save(prefs)
    toast({
      title: 'Horário de notificações',
      description: ok ? 'Preferências salvas com sucesso.' : 'Não foi possível salvar suas preferências.',
      variant: ok ? 'success' : 'error',
    })
  }

  async function handleSnooze(ms: number) {
    if (!prefs) return
    const until = new Date(Date.now() + ms).toISOString()
    const next = { ...prefs, snoozeUntil: until }
    const ok = await save(next)
    toast({
      title: 'Pausar notificações',
      description: ok ? 'Notificações pausadas temporariamente.' : 'Não foi possível pausar agora.',
      variant: ok ? 'success' : 'error',
      soft: true,
    })
  }

  async function handleClearSnooze() {
    if (!prefs) return
    const next = { ...prefs, snoozeUntil: null }
    const ok = await save(next)
    toast({
      title: 'Pausar notificações',
      description: ok ? 'Notificações retomadas.' : 'Não foi possível retomar agora.',
      variant: ok ? 'success' : 'error',
      soft: true,
    })
  }

  function handleModeChange(mode: PushScheduleMode) {
    update((current) => ({
      ...current,
      mode,
    }))
  }

  function handleDefaultTimeChange(field: 'start' | 'end', value: string) {
    const minutes = optionValueToMinute(value)
    update((current) => {
      const next = { ...current }
      if (field === 'start') {
        next.defaultStartMin = minutes
      } else {
        next.defaultEndMin = minutes
      }
      return next
    })
  }

  function handleCustomDayTimeChange(day: PushDayKey, field: 'start' | 'end', value: string) {
    const minutes = optionValueToMinute(value)
    update((current) => {
      const next = { ...current, days: { ...current.days } }
      const dayCfg = next.days[day]
      next.days[day] = {
        ...dayCfg,
        [field === 'start' ? 'startMin' : 'endMin']: minutes,
      }
      return next
    })
  }

  function handleCustomDayToggle(day: PushDayKey, enabled: boolean) {
    update((current) => {
      const next = { ...current, days: { ...current.days } }
      next.days[day] = { ...next.days[day], enabled }
      return next
    })
  }

  const snoozeText = prefs ? formatSnoozeDescription(prefs.snoozeUntil, formatUserDateTime) : null

  return (
    <Card>
      <CardHeader className="text-sm font-medium">Horário de notificações</CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Pausar notificações (rápido) */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold">Pausar notificações</Label>
            <div className="flex flex-wrap gap-2">
              {SNOOZE_PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  type="button"
                  variant="outline"
                  disabled={busy}
                  onClick={() => handleSnooze(preset.ms)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
            <div className="flex items-center justify-between pt-1 text-xs text-foreground/70">
              <div>
                {snoozeText
                  ? `Notificações pausadas até ${snoozeText}.`
                  : 'Você pode pausar temporariamente as notificações neste dispositivo.'}
              </div>
              {prefs?.snoozeUntil && (
                <button
                  type="button"
                  className="text-xs text-primary underline-offset-2 hover:underline"
                  onClick={handleClearSnooze}
                  disabled={busy}
                >
                  Retomar agora
                </button>
              )}
            </div>
          </div>

          {/* Horário recorrente */}
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs font-semibold">Permitir notificações</Label>
              <Select
                value={prefs?.mode || 'all_days'}
                onChange={(e) => handleModeChange(e.target.value as PushScheduleMode)}
                disabled={busy}
                className="h-10 max-w-xs"
              >
                <option value="all_days">Todos os dias</option>
                <option value="weekdays">Dias da semana</option>
                <option value="custom">Personalizado por dia</option>
                <option value="off">Desativado</option>
              </Select>
            </div>

            {(prefs?.mode === 'all_days' || prefs?.mode === 'weekdays') && (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span>Das</span>
                <Select
                  value={minuteToOptionValue(prefs.defaultStartMin)}
                  onChange={(e) => handleDefaultTimeChange('start', e.target.value)}
                  disabled={busy}
                  className="h-10 w-28"
                >
                  {TIME_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
                <span>até</span>
                <Select
                  value={minuteToOptionValue(prefs.defaultEndMin)}
                  onChange={(e) => handleDefaultTimeChange('end', e.target.value)}
                  disabled={busy}
                  className="h-10 w-28"
                >
                  {TIME_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </div>
            )}

            {prefs?.mode === 'custom' && (
              <div className="space-y-2">
                {(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as PushDayKey[]).map((dayKey) => {
                  const cfg = prefs.days[dayKey]
                  const timesOpen = cfg.enabled
                  return (
                    <div key={dayKey} className="w-full text-sm">
                      <div
                        className={[
                          'w-full rounded-md border px-3 py-1.5 transition-colors',
                          'bg-background',
                          cfg.enabled ? 'border-border bg-primary/5 text-foreground' : 'border-border bg-muted/40 text-foreground/70',
                        ].join(' ')}
                      >
                        <div className="flex w-full items-center justify-between">
                          <span className="text-xs font-medium whitespace-nowrap">{DAY_LABELS[dayKey]}</span>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] uppercase tracking-wide">
                              {cfg.enabled ? 'Ativo' : 'Pausado'}
                            </span>
                            <Switch
                              checked={cfg.enabled}
                              onChange={(e) => handleCustomDayToggle(dayKey, (e.target as HTMLInputElement).checked)}
                              disabled={busy}
                            />
                          </div>
                        </div>
                        <div
                          className={[
                            'mt-2 overflow-hidden text-xs text-foreground/70 transition-all duration-200',
                            timesOpen ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0',
                          ].join(' ')}
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs text-foreground/70">Das</span>
                            <Select
                              value={minuteToOptionValue(cfg.startMin)}
                              onChange={(e) => handleCustomDayTimeChange(dayKey, 'start', e.target.value)}
                              disabled={busy || !cfg.enabled}
                              className="h-9 w-24"
                            >
                              {TIME_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </Select>
                            <span className="text-xs text-foreground/70">até</span>
                            <Select
                              value={minuteToOptionValue(cfg.endMin)}
                              onChange={(e) => handleCustomDayTimeChange(dayKey, 'end', e.target.value)}
                              disabled={busy || !cfg.enabled}
                              className="h-9 w-24"
                            >
                              {TIME_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </Select>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            <p className="text-xs text-foreground/70">
              Fora dos horários configurados, as notificações Web Push ficam em modo silencioso. O horário considera o seu
              fuso configurado em &quot;Localização e horário&quot;.
            </p>

            <div className="pt-1">
              <Button type="button" onClick={handleSave} disabled={busy} className="min-w-32">
                Salvar alterações
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default PushScheduleSettingsCard
